import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { Clock, Newspaper, CaretRight, FunnelSimple } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function AllNewsPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [region, setRegion] = useState("all");
  const [category, setCategory] = useState("all");

  const fetchArticles = useCallback(async (pageNum = 1, reset = false) => {
    try {
      const params = { limit: 50, page: pageNum };
      if (region !== "all") params.region = region;
      if (category !== "all") params.category = category;
      
      const res = await axios.get(`${API}/api/articles`, { params });
      const data = Array.isArray(res.data) ? res.data : (res.data.articles || []);
      
      if (reset || pageNum === 1) {
        setArticles(data);
      } else {
        setArticles(prev => [...prev, ...data]);
      }
      setHasMore(data.length === 50);
      setPage(pageNum);
    } catch (err) {
      console.error("Error fetching articles:", err);
    } finally {
      setLoading(false);
    }
  }, [region, category]);

  useEffect(() => {
    setLoading(true);
    fetchArticles(1, true);
  }, [region, category, fetchArticles]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 48) return "Yesterday";
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  const getRegionFlag = (region) => {
    const flags = {
      ireland: "🇮🇪",
      uk: "🇬🇧",
      usa: "🇺🇸",
      global: "🌍",
      world: "🌍",
      brazil: "🇧🇷",
      france: "🇫🇷",
      india: "🇮🇳",
      china: "🇨🇳",
      mexico: "🇲🇽"
    };
    return flags[region?.toLowerCase()] || "📰";
  };

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <SEO title="All Farming News Headlines" description="Directory of all the latest farming news headlines from Ireland, UK, USA and worldwide in one place. Updated throughout the day." url="/all-news" />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#022C22] to-[#064E3B] py-6">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2">
            <Newspaper size={28} className="text-[#5EEAD4]" />
            <h1 className="font-serif text-2xl sm:text-3xl text-white">
              All News Headlines
            </h1>
          </div>
          <p className="text-[#A7F3D0] text-sm">
            Complete news feed from Ireland, UK, USA & around the world
          </p>
        </div>
      </section>

      {/* Filters */}
      <div className="bg-white border-b border-[#E0D4C8] sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <FunnelSimple size={16} className="text-[#6B6661]" />
              <span className="text-xs font-mono uppercase tracking-wider text-[#6B6661]">Filter:</span>
            </div>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="px-3 py-1.5 border border-[#E0D4C8] text-sm bg-white focus:outline-none focus:border-[#0F766E]"
            >
              <option value="all">All Regions</option>
              <option value="ireland">🇮🇪 Ireland</option>
              <option value="uk">🇬🇧 United Kingdom</option>
              <option value="usa">🇺🇸 United States</option>
              <option value="world">🌍 Global</option>
            </select>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="px-3 py-1.5 border border-[#E0D4C8] text-sm bg-white focus:outline-none focus:border-[#0F766E]"
            >
              <option value="all">All Categories</option>
              <option value="general">General</option>
              <option value="markets">Markets</option>
              <option value="livestock">Livestock</option>
              <option value="crops">Crops</option>
              <option value="agri-tech">Agri-Tech</option>
            </select>
            <span className="text-xs text-[#6B6661] ml-auto">
              {articles.length} headlines
            </span>
          </div>
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F766E]"></div>
          </div>
        ) : (
          <>
            {/* Headlines List */}
            <div className="bg-white border border-[#E0D4C8]">
              {articles.map((article, index) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className={`flex items-start gap-4 p-4 hover:bg-[#F6E9DD] transition-colors ${
                    index !== articles.length - 1 ? "border-b border-[#E0D4C8]" : ""
                  }`}
                >
                  {/* Region Flag */}
                  <span className="text-lg flex-shrink-0 mt-0.5">
                    {getRegionFlag(article.region)}
                  </span>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h2 className="font-serif text-base sm:text-lg text-[#33302E] leading-snug group-hover:text-[#0F766E] line-clamp-2">
                      {article.title}
                    </h2>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-[#6B6661]">
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {formatDate(article.published_at)}
                      </span>
                      <span className="px-1.5 py-0.5 bg-[#E5E7EB] text-[10px] uppercase tracking-wider">
                        {article.category || "News"}
                      </span>
                      <span className="truncate max-w-[150px]">{article.source}</span>
                    </div>
                  </div>
                  
                  {/* Arrow */}
                  <CaretRight size={18} className="text-[#0F766E] flex-shrink-0 mt-1" />
                </Link>
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="text-center mt-6">
                <button
                  onClick={() => fetchArticles(page + 1)}
                  className="px-6 py-3 bg-[#0F766E] text-white font-medium hover:bg-[#064E3B] transition-colors"
                >
                  Load More Headlines
                </button>
              </div>
            )}

            {articles.length === 0 && (
              <div className="text-center py-12 text-[#6B6661]">
                <Newspaper size={48} className="mx-auto mb-4 opacity-50" />
                <p>No articles found matching your filters.</p>
              </div>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
